import { Badge, Button, Card } from "@procurement-agents/ui";
import { displayEndId, displayEndIdsIn, type PlanEntryEdit, type RunPlan, type RunPlanEntry, type SectionPlanReviewPayload, type SectionPlanReviewResume } from "@procurement-agents/scope-api";
import { Fragment, useEffect, useRef, useState } from "react";
import { LegacyPlanReview } from "./LegacyPlanReview";
import { childrenOf, included, merge, MIN_LIFT_REASON, move, numbered, orderPlan, requiredBy, slug, split, toResume, validate } from "./planEdit";
import { LevelOfDetail } from "./LevelOfDetail";

export { renumber } from "./planEdit";

/** Template names run long; the card shows the name without its parenthetical, and the whole of it on hover. */
const shortName = (name: string) => {
  const short = name.replace(/\s*\([^)]*\)\s*$/, "").trim() || name;
  return short.length > 58 ? `${short.slice(0, 57)}…` : short;
};

/** A template's name as the picker shows it: short, unless two would then read the same (then in full). */
const optionName = (o: { id: string; name: string }, all: Array<{ name: string }>) =>
  all.filter((x) => shortName(x.name) === shortName(o.name)).length > 1 ? o.name : shortName(o.name);

/** A left-out section's title without the template's number: numbers belong to the sections in the plan. */
const plainTitle = (title: string) => title.replace(/^\d+(?:\.\d+)*[A-Za-z]?\.?\s+/, "");

/**
 * CHANGE_plan_chat: the section plan as an editor. Include or leave out, reorder within a part, adjust the note the
 * planner wrote, override a section's instructions for this run, and add a section the template lacks. Nothing here
 * touches the template. The agent proposes into the same list through the chat drawer.
 */
export function PlanReview({ payload, plan, onPlanChange, onSubmit, busy, onAsk, proposalIds = [], restricted, onHandOff }: {
  payload: SectionPlanReviewPayload; onSubmit: (value: SectionPlanReviewResume) => void; busy: boolean;
  plan?: RunPlan | null; onPlanChange?: (p: RunPlan) => void; onAsk?: (seed?: string) => void; proposalIds?: string[];
  /** CHANGE_self_service: the requester is driving. What is procurement's is absent, not disabled. */
  restricted?: boolean; onHandOff?: () => void;
}) {
  const initial = payload.run_plan ?? null;
  const [own, setOwn] = useState<RunPlan | null>(() => (initial ? orderPlan(initial, payload.section_order ?? []) : null));
  const current = plan ?? own;
  const set = (next: RunPlan) => { setOwn(next); onPlanChange?.(next); };
  useEffect(() => { if (plan) setOwn(plan); }, [plan]);
  const [adding, setAdding] = useState(false);
  const [showLeftOut, setShowLeftOut] = useState(false);
  const [merging, setMerging] = useState<string[] | null>(null);        // CHANGE_plan_control: rows picked to merge
  const [mergeTitle, setMergeTitle] = useState("");
  const [lifting, setLifting] = useState<string | null>(null);          // the required row procurement is leaving out
  const [liftReason, setLiftReason] = useState("");
  const [switchTo, setSwitchTo] = useState<string | null>(null);
  const dragging = useRef<string | null>(null);

  if (!initial || !current) return <LegacyPlanReview payload={payload} onSubmit={onSubmit} busy={busy} />;

  const rows = included(current);
  const traced = current.entries.some((e) => (e.serves?.length ?? 0) > 0);   // planned under CHANGE_outcome_traceability
  // KRE-121: a section merged away is part of its merged row (Split again brings it back), not one left out
  const leftOut = current.entries.filter((e) => !e.included && !e.merged_into);
  const wasIn = new Set((initial?.entries ?? []).filter((e) => e.included).map((e) => e.id));   // in the plan this review started from
  const numbers = numbered(rows);
  const errors = validate(current, !restricted);
  const byTemplate = requiredBy(current);
  const options = restricted ? [] : payload.template_options ?? [];
  const changed = JSON.stringify(current) !== JSON.stringify(orderPlan(initial, payload.section_order ?? []));

  const update = (id: string, fields: Partial<RunPlanEntry>) =>
    set({ ...current, entries: current.entries.map((e) => (e.id === id ? { ...e, ...fields } : e)) });
  const setIncluded = (e: RunPlanEntry, on: boolean) => {
    const family = [e.id, ...childrenOf(current, e.id).map((c) => c.id)];
    set({ ...current, entries: current.entries.map((x) => (family.includes(x.id) ? { ...x, included: on && (x.id === e.id || x.locked !== "filtered") } : x)) });
  };
  const addSection = (title: string, guidance: string, length: "short" | "medium" | "long") => {
    const id = slug(title);
    if (current.entries.some((e) => e.id === id)) return;
    const entry: RunPlanEntry = { id, title: title.trim(), parent: null, included: true, is_adhoc: true, locked: null, reason: "added by procurement", guidance: "", guidance_override: guidance.trim(), length, word_target: 0, focus_note: null, sme_domain: null };
    set({ ...current, entries: [...rows, entry, ...leftOut] });
    setAdding(false);
  };
  const submit = () => onSubmit(changed ? toResume(current, proposalIds, null) : "approve");
  const lift = (e: RunPlanEntry) => { update(e.id, { included: false, lifted_reason: liftReason.trim() }); setLifting(null); setLiftReason(""); };
  const restore = (e: RunPlanEntry) => e.lifted_reason ? set({ ...current, entries: current.entries.map((x) => (x.id === e.id ? { ...x, included: true, lifted_reason: null } : x)) }) : setIncluded(e, true);
  const doMerge = () => { if (merging) set(merge(current, merging, mergeTitle)); setMerging(null); setMergeTitle(""); };
  const lockTitle = (e: RunPlanEntry) => e.policy_text
    ? `Required by ${byTemplate}, with the customer's mandatory text. Change it under Templates, or merge it into another section.`
    : `Required by ${byTemplate}. Change it under Templates${restricted ? "" : ", or leave it out of this request with a reason"}.`;

  return (
    <Card title={<span title={payload.template_name}>Sections proposed for “{shortName(payload.template_name)}”</span>}
          aside={onAsk ? <Button variant="quiet" onClick={() => onAsk()} disabled={busy}>Ask the agent</Button> : undefined}>
      <p className="muted small template-line">
        From the {payload.template_name} template{payload.template_version ? `, version ${payload.template_version}` : ""}.
        {options.length > 1 && <> <label className="inline">Plan from another template:{" "}
          <select value={switchTo ?? payload.template_id} onChange={(ev) => setSwitchTo(ev.target.value === payload.template_id ? null : ev.target.value)} disabled={busy} aria-label="Template">
            {options.map((o) => <option key={o.id} value={o.id}>{optionName(o, options)}{o.version ? ` (v${o.version})` : ""}</option>)}
          </select></label></>}
      </p>
      {switchTo && (
        <span className="row confirm-row">
          <span className="small">Plan again from {(() => { const o = options.find((x) => x.id === switchTo); return o ? optionName(o, options) : switchTo; })()}? The sections are proposed afresh and changes made here are lost.</span>
          <Button variant="primary" disabled={busy} onClick={() => onSubmit({ action: "switch_template", template_id: switchTo })}>Plan from this template</Button>
          <Button variant="quiet" onClick={() => setSwitchTo(null)}>Cancel</Button>
        </span>
      )}
      <p className="muted small">Leave out what this request does not need, reorder within a part, and say what each section must cover. The template is not changed.</p>
      {payload.verbosity && !restricted && <LevelOfDetail level={payload.verbosity} reason={payload.verbosity_reason} />}
      <ol className="plan">
        {rows.map((e, i) => (<Fragment key={e.id}>
          {e.addendum && !rows[i - 1]?.addendum && <li className="addenda-divider" aria-hidden>Addenda — standard terms <span className="muted small">drafted short, checked by the fixed rules only</span></li>}
          <li className={`plan-row${e.parent ? " sub" : ""}${e.is_adhoc ? " adhoc" : ""}`}
              draggable={!busy && !e.parent} onDragStart={() => { dragging.current = e.id; }} onDragOver={(ev) => ev.preventDefault()}
              onDrop={() => { if (dragging.current && dragging.current !== e.id) set(move(current, dragging.current, e.id)); dragging.current = null; }}>
            <div className="row-head">
              <span className="row-left">
                {!e.parent && <span className="drag-handle" aria-hidden title="Drag to reorder">⠿</span>}
                {merging
                  ? <input type="checkbox" checked={merging.includes(e.id)} disabled={busy} onChange={(ev) => setMerging(ev.target.checked ? [...merging, e.id] : merging.filter((x) => x !== e.id))} aria-label={`Merge ${e.title}`} />
                  : <input type="checkbox" checked disabled={busy || !!e.locked} onChange={() => setIncluded(e, false)} aria-label={e.title} />}
                <span className="row-title">{numbers.get(e.id) ?? e.title}</span>
                {e.parent ? (e.locked && <Badge>with parent</Badge>) : (e.locked === "required" && <span title={lockTitle(e)}><Badge>required</Badge></span>)}
                {e.merged_from?.length ? <Badge tone="info">merged</Badge> : (e.is_adhoc || e.guidance_override) && <Badge tone="info">{e.is_adhoc ? "new for this run" : "adjusted for this run"}</Badge>}
                {/* CHANGE_outcome_traceability: the ends each section serves; a required one serving none says so (evidence for the template's owner) */}
                {(e.serves?.length ?? 0) > 0 && <span className="serves" aria-label={`Serves ${e.serves!.map(displayEndId).join(", ")}`}>{e.serves!.map((id) => <span key={id} className="end-tag">{displayEndId(id)}</span>)}</span>}
                {traced && !e.serves?.length && !e.addendum && !e.is_adhoc && <Badge tone="neutral">serves no stated outcome</Badge>}
              </span>
              <span className="row-right">
                {/* a section's length comes from its template (decision 5 of 28 Sep: nothing per request); the target is shown, not chosen */}
                {!!e.word_target && <span className="word-target" title="Set by the template">~{e.word_target} words</span>}
                {!e.parent && (
                  <span className="plan-move">
                    <button type="button" className="btn quiet small" onClick={() => set(move(current, e.id, i >= 2 ? rows[i - 2]!.id : null))} disabled={busy || i === 0} aria-label={`Move ${e.title} up`} title="Move up">↑</button>
                    <button type="button" className="btn quiet small" onClick={() => set(move(current, e.id, rows[i + 1]?.id ?? null))} disabled={busy || i === rows.length - 1} aria-label={`Move ${e.title} down`} title="Move down">↓</button>
                  </span>
                )}
              </span>
            </div>
            {e.reason && <p className="row-reason">{displayEndIdsIn(e.reason)}{e.merged_from?.length ? <> · <button type="button" className="link" onClick={() => set(split(current, e.id))} disabled={busy}>Split again</button></> : null}</p>}
            {e.locked === "required" && !e.parent && !merging && (
              <p className="row-lock" title={lockTitle(e)}>
                {e.policy_text ? "Required by the template, with policy text · change under Templates, or merge" : "Required by the template · change under Templates"}
                {!restricted && !e.policy_text && lifting !== e.id && <> · <button type="button" className="link" onClick={() => { setLifting(e.id); setLiftReason(""); }} disabled={busy}>Leave out of this request…</button></>}
              </p>
            )}
            {lifting === e.id && (
              <form className="composer lift" aria-label={`Leave out ${e.title}`} onSubmit={(ev) => { ev.preventDefault(); lift(e); }}>
                <input value={liftReason} onChange={(ev) => setLiftReason(ev.target.value)} placeholder="Why this request does not need it" aria-label={`Why ${e.title} is not needed`} autoFocus />
                <Button variant="primary" type="submit" disabled={liftReason.trim().length < MIN_LIFT_REASON}>Leave out</Button>
                <Button variant="quiet" type="button" onClick={() => setLifting(null)}>Cancel</Button>
                <span className="muted small">Recorded with your reason and shown with the plan.</span>
              </form>
            )}
            <SectionRow entry={e} busy={busy} restricted={restricted} onNote={(v) => update(e.id, { focus_note: v })} onGuidance={(v) => update(e.id, { guidance_override: v })} />
          </li>
        </Fragment>))}
      </ol>
      {merging && (
        <form className="composer merge" aria-label="Merge sections" onSubmit={(ev) => { ev.preventDefault(); doMerge(); }}>
          <span className="small">{merging.length < 2 ? "Tick two or more sections above to merge them." : `Merge ${merging.length} sections into one. Their instructions, notes, policy text and tables are kept.`}</span>
          <input value={mergeTitle} onChange={(ev) => setMergeTitle(ev.target.value)} placeholder="Title of the merged section" aria-label="Merged section title" />
          <Button variant="primary" type="submit" disabled={merging.length < 2 || !mergeTitle.trim()}>Merge</Button>
          <Button variant="quiet" type="button" onClick={() => { setMerging(null); setMergeTitle(""); }}>Cancel</Button>
        </form>
      )}
      <div className="row">
        {!adding && <Button variant="quiet" onClick={() => setAdding(true)} disabled={busy}>Add a section</Button>}
        {!merging && rows.length > 1 && <Button variant="quiet" onClick={() => setMerging([])} disabled={busy}>Merge sections</Button>}
        {leftOut.length > 0 && <button type="button" className="link small" onClick={() => setShowLeftOut((v) => !v)} aria-expanded={showLeftOut}>{showLeftOut ? "Hide" : "Show"} the {leftOut.length} sections left out</button>}
      </div>
      {adding && <AddSection onAdd={addSection} onCancel={() => setAdding(false)} />}
      {showLeftOut && (
        <ul className="plan left-out">
          {leftOut.map((e) => (
            <li key={e.id}>
              <span className="row">
                <strong className="muted">{plainTitle(e.title)}</strong>
                {e.locked === "filtered" ? <Badge tone="neutral">does not apply</Badge>
                  : e.locked === "with_parent" ? <Badge tone="neutral">follows its parent</Badge>
                  : e.merged_into ? <Badge tone="neutral">merged</Badge>
                  : <button type="button" className="link small" onClick={() => restore(e)} disabled={busy}>Include</button>}
                {e.lifted_reason && <Badge tone="warn">required, left out</Badge>}
              </span>
              {/* KRE-121: unticked here, it says so at once, not the planner's reason for including it */}
              <p className="muted small">{e.lifted_reason ? `Left out by procurement: ${e.lifted_reason}` : wasIn.has(e.id) ? "Left out on this review." : e.reason}</p>
            </li>
          ))}
        </ul>
      )}
      {errors.length > 0 && <aside className="validation-panel bad" aria-live="polite"><strong>Not ready to start drafting:</strong><ul>{errors.map((x, i) => <li key={i}>{x}</li>)}</ul></aside>}
      <div className="row">
        <Button variant="primary" onClick={submit} disabled={busy || errors.length > 0}>Approve plan and start drafting</Button>
        {onHandOff && <button type="button" className="link small" onClick={onHandOff} disabled={busy}>Hand to procurement</button>}
        {changed && <button type="button" className="link small" onClick={() => { setOwn(orderPlan(initial, payload.section_order ?? [])); onPlanChange?.(orderPlan(initial, payload.section_order ?? [])); }} disabled={busy}>Discard my changes</button>}
      </div>
    </Card>
  );
}

/** One section below its header: the note, the line that labels it, and the template instructions behind a
 *  disclosure. Everything the reviewer reads lines up on one left edge; everything they act on is above. */
function SectionRow({ entry, busy, onNote, onGuidance, restricted }: { entry: RunPlanEntry; busy: boolean; onNote: (v: string | null) => void; onGuidance: (v: string | null) => void; restricted?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(entry.is_adhoc);
  const note = entry.focus_note ?? "";
  const long = note.length > 150;
  const overridden = entry.guidance_override != null;
  const rows = expanded ? Math.min(10, Math.max(3, Math.ceil(note.length / 95) + 1)) : 2;
  return (
    <>
      <textarea className={`row-note${long && !expanded ? " clipped" : ""}`} rows={rows} value={note} disabled={busy}
        onFocus={() => setExpanded(true)} onBlur={() => setExpanded(false)} onChange={(e) => onNote(e.target.value || null)}
        placeholder="What this request needs from this section (optional)" aria-label={`Note for ${entry.title}`} />
      <div className="row-foot">
        <span className="foot-label">
          Note for drafting{long && !expanded && <> · <button type="button" className="link" onClick={() => setExpanded(true)}>more</button></>}
        </span>
        <span className="foot-actions">
          {restricted && !entry.is_adhoc && <span className="muted">from the template</span>}
          {overridden && !entry.is_adhoc && !restricted && <><button type="button" className="link" onClick={() => onGuidance(null)} disabled={busy}>Reset</button> · </>}
          {(!restricted || entry.is_adhoc) && <button type="button" className="link disclosure" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            <span className="tri" aria-hidden>{open ? "▾" : "▸"}</span>
            {entry.is_adhoc ? "What this section must cover" : overridden ? "Template instructions edited" : "Edit the template instructions"}
          </button>}
        </span>
      </div>
      {open && (
        <textarea className="row-guidance" rows={5} value={entry.guidance_override ?? entry.guidance ?? ""} disabled={busy}
          onChange={(e) => onGuidance(e.target.value)} aria-label={`Instructions for ${entry.title}`} />
      )}
    </>
  );
}

function AddSection({ onAdd, onCancel }: { onAdd: (title: string, guidance: string, length: "short" | "medium" | "long") => void; onCancel: () => void }) {
  const [title, setTitle] = useState("");
  const [guidance, setGuidance] = useState("");
  return (
    <form className="composer add-section" aria-label="Add a section" onSubmit={(e) => { e.preventDefault(); onAdd(title, guidance, "medium"); }}>
      <div className="row wrap">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Section title" required aria-label="New section title" />
      </div>
      <textarea rows={3} value={guidance} onChange={(e) => setGuidance(e.target.value)} placeholder="What this section must cover" required aria-label="New section instructions" />
      <div className="row"><Button variant="primary" type="submit" disabled={!title.trim() || !guidance.trim()}>Add for this run</Button><Button variant="quiet" type="button" onClick={onCancel}>Cancel</Button></div>
    </form>
  );
}

/** A plan proposal from the agent: what it would change, and Apply, which puts it in the list above. */
export function planProposalLines(result: PlanEntryEdit[] | undefined, before: RunPlan): string[] {
  const now = new Set((result ?? []).map((e) => e.id));
  const was = new Set(included(before).map((e) => e.id));
  const title = (id: string) => before.entries.find((e) => e.id === id)?.title ?? id;
  return [
    ...[...now].filter((id) => !was.has(id)).map((id) => `add ${title(id)}`),
    ...[...was].filter((id) => !now.has(id)).map((id) => `leave out ${title(id)}`),
  ];
}
